import React, { Fragment, useEffect, useState } from 'react';
import Config from '../../config';
import axios from 'axios';
import Helper, {formatHoursWithDays, getHostedThumbnail} from '../../helpers';
import {
    Typography,
    Grid,
    Card,
    Toolbar,
    CardContent,
    Button,
    Dialog,
    DialogContent,
    DialogActions,
    ButtonBase,
    TablePagination,
    Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ContentLoader from '../../components/content_loader';
import Image from '../../components/image';
import { SimpleRte } from '../../components/EnhancedRte';
import SearchBox from '../../components/searchbox';
import { AddAPhoto as NoImageIcon } from '@mui/icons-material';
import '../inventory/v1/product_feed.css';
import { Alert } from '@mui/material';
import shipment_time from '../../images/shipment_time.png';
import top_seller from '../../images/top_seller.png';
import { ErrorOutline as LoadErrorIcon } from '@mui/icons-material';

const StyledCardContent = styled(CardContent)({
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
    '& > div': {
        marginBottom: '0.5em'
    }
});

/** Company/network profile shape returned by `/odata/Company/CompanyProfiles`. */
export interface CompanyProfileData {
    CoId?: number | string;
    CompanyName?: string;
    Description?: string;
    Website?: string;
    Categories?: string[];
    Subcategory?: string;
    IsPartner?: boolean;
    MerchFirstName?: string;
    MerchLastName?: string;
    MerchEmail?: string;
    TopSeller?: boolean;
    ShipmentTime?: number;
    ShipmentTimeTarget?: number;
    ShipmentTimeLetter?: string;
    [key: string]: unknown;
}

export interface NetworkCatalogItem {
    id?: string | number;
    name?: string;
    Name?: string;
    image?: string;
    description?: string;
    category?: string;
    [key: string]: unknown;
}

export interface ContactDialogProps {
    open: boolean;
    companyName?: string;
    disabled?: boolean;
    onClose: () => void;
    merchWarning?: string | false;
    message?: string;
    messageEmpty?: boolean;
    onMessageChange: (html: string, isEmpty: boolean) => void;
    submit: () => void;
    sentMessage?: string | boolean | null;
}

const ContactDialog = (props: ContactDialogProps) => {
    let { open, companyName, disabled, onClose, merchWarning, message, messageEmpty, onMessageChange, submit, sentMessage } = props;
    return <Dialog fullWidth={true} maxWidth='md' open={open} onClose={() => !disabled && onClose()}>
    <Toolbar className='lbtoolbar'>Connect</Toolbar>
    {!(!sentMessage) ? <DialogContent style={{flexDirection: 'column'}}>
        <Typography>{sentMessage}</Typography>
        </DialogContent> : <Fragment><DialogContent style={{flexDirection: 'column'}}>
        <Alert severity="info" style={{marginBottom: '1em'}}>
            <Typography>
                This message will be sent to all Logicbroker users at {companyName}.
            </Typography>
        </Alert>
        {!(!merchWarning) && <Alert severity="error" style={{marginBottom: '1em'}}>
            <Typography>{merchWarning}</Typography>
        </Alert>}
    <div style={{height: '200px', display: 'flex'}}>
    <SimpleRte className='textEditor' disabled={disabled} value={message} onChange={(html: string, isEmpty: boolean) => onMessageChange(html, isEmpty)}/>
    </div>
</DialogContent>
<DialogActions>
    <Button onClick={submit} disabled={messageEmpty || disabled} variant="text" color="primary">
      Send
    </Button>
</DialogActions>
</Fragment>}
</Dialog>;
}

export interface CompanyProfileProps {
    isRetailer?: boolean | number;
    setTitle?: (title: string, subtitle?: boolean) => void;
}

const CompanyProfile = (props: CompanyProfileProps) => {
    const [showMessage, setShowMessage] = useState<boolean>(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [products, setProducts] = useState<NetworkCatalogItem[]>([]);
    const [showProduct, setShowProduct] = useState<boolean>(false);
    const [product, setProduct] = useState<NetworkCatalogItem>({});
    const [profile, setProfile] = useState<CompanyProfileData>({});
    const [items, setItems] = useState<NetworkCatalogItem[]>([]);
    const [term, setTerm] = useState<string>('');
    const [page, setPage] = useState<number>(0);
    const [total, setTotal] = useState<number>(0);
    const [rowsPerPage] = useState<number>(50);
    const [loaded, setLoaded] = useState<boolean | undefined>(undefined);
    const [loadError, setLoadError] = useState<boolean | undefined>(undefined);
    const [disableSend, setDisableSend] = useState<boolean | undefined>(undefined);
    const [messageSent, setMessageSent] = useState<boolean | string | undefined>(undefined);
    const [message, setMessage] = useState<string | undefined>(undefined);
    const [messageEmpty, setMessageEmpty] = useState<boolean | undefined>(undefined);

    // `profileArg`/`pageArg` let callers pass freshly-computed values that have
    // not yet been flushed to state (mount load, page change), matching the
    // class's `await this.setState(...)` followed by `loadItems()`.
    const loadItems = async (profileArg: CompanyProfileData = profile, pageArg: number = page) => {
        let searchTerm = term ? term : '*';
        let skip = rowsPerPage * pageArg;
        let itemRes = (await axios.get(Config.api + `/odata/Company/Functions.NetworkCatalogSearch?supplierId=${profileArg.CoId}&term=${searchTerm}&$top=${rowsPerPage}&$skip=${skip}&$orderby=name asc&$count=true`)).data;
        let loadedItems = itemRes.value;
        setItems(loadedItems);
        setTotal(itemRes["@odata.count"]);
    }

    useEffect(() => {
        (async () => {
            let id = new Helper().queryString()["id"];
            let profileData = null;
            try{
                profileData = (await axios.get(Config.api + `/odata/Company/CompanyProfiles(${id})`)).data.value[0];
            }catch{
                setProfile({});
                setLoaded(true);
                setLoadError(true);
                return;
            }
            setProfile(profileData);
            setLoaded(true);
            if(props.setTitle && profileData && profileData.CompanyName) {
                props.setTitle(`Discover: ${profileData.CompanyName}`, true);
            }
            if(props.isRetailer){
                await loadItems(profileData);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const createThread = async () => {
        try {
            setDisableSend(true);
            await axios.post(Config.api + '/odata/Company/Functions.NewThread', {
                Body: message,
                RequireAck: false,
                Recipients: [profile.CoId]
            });
            setMessageSent(true);
            setDisableSend(false);
        }catch(e){

        }
    }
    const newThread = () => {
        setDisableSend(false);
        setMessageSent(false);
        setShowMessage(true);
        setMessage('');
        setMessageEmpty(true);
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const onEnter = async (e: React.SyntheticEvent<HTMLInputElement>) => {
        var newTerm = e.currentTarget.value;
        setTerm(newTerm);
    }
    const updateTerm = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTerm(e.target.value);
    }

    const searchItems = () => {
        loadItems();
    }
    const handleChangePage = async (event: unknown, nextPage: number) => {
        setPage(nextPage);
        loadItems(profile, nextPage);
    };
    const getLink = (website?: string) => {
        if(website && (website.indexOf('http:') === 0 || website.indexOf('https:') === 0)){
            return website;
        }
        return 'https://' + website;
    }

    let { isRetailer } = props;
    let retailerSupplier = isRetailer ? "Supplier" : "Retailer";
    if(loadError){
        return <Grid container spacing={2}>
            <Grid size={12}>
              <LoadErrorIcon style={{fontSize: '14rem', color: '#999'}}/>
            </Grid>
            <Grid size={12} style={{fontSize: '1.5rem', color: '#999'}}>Sorry, you do not have permission to view this profile.</Grid>
      </Grid>;
    }
    return (
        <Grid container spacing={2}>
            <Grid size={{ sm: 12, md: 8, lg: 8 }} style={{display: 'flex'}}>
            {loaded ? <Card style={{flex: 1}}>
                <Toolbar className='lbtoolbar'>{retailerSupplier} Profile for {profile.CompanyName}</Toolbar>
                <StyledCardContent>
                <div style={{margin: 0}}><Image style={{maxHeight: '100px'}} src={`${Config.logos}${profile.CoId}large.jpg`}/></div>
            {profile.Description ? <Fragment><div dangerouslySetInnerHTML={{__html: profile.Description}}></div>
            </Fragment>
            : <div>This {retailerSupplier.toLowerCase()} has no profile available.</div>}
            <div style={{marginTop: '1em'}}>
            </div>
            </StyledCardContent></Card>
            : <ContentLoader preserveAspectRatio='none' style={{minHeight: '10em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>}
            </Grid>
            <Grid size={{ sm: 12, md: 4, lg: 4 }} style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                <Grid size={{ sm: 12, md: 12, lg: 12 }} style={{display: 'flex'}}>
                    {loaded ? <Card style={{flex: 1}}>
                    <Toolbar className='lbtoolbar'>Basics</Toolbar>
                    <StyledCardContent>
                        <Typography variant='h6'>{profile.CompanyName}</Typography>
                        {!(!profile.MerchEmail) && <Typography color='textSecondary' style={{fontSize: '13px'}}><span style={{fontWeight: 'bold'}}>Contact:</span> {profile.MerchFirstName} {profile.MerchLastName} <a href={`mailto:${profile.MerchEmail}`}>{profile.MerchEmail}</a></Typography>}
                    <div style={{display: 'flex', gap: '0.5em', flexWrap: 'wrap', paddingTop: '0.5em', paddingBottom: '0.5em'}}>
                                    {(profile.Categories || []).map(c => <Chip variant='outlined' size='small' key={c} label={c}/>)}
                    </div>
                    {!(!profile.Subcategory) && <Typography color='textSecondary' style={{fontSize: '13px'}}><span style={{fontWeight: 'bold'}}>Subcategory:</span> {profile.Subcategory}</Typography>}
                    <div style={{display: 'flex', gap: '1em'}}>
                        <Button component='a' variant='outlined' disabled={!profile.Website} href={getLink(profile.Website)} target='_blank' rel='noopener noreferrer'>View Shop</Button>
                        <Button variant='contained' color='primary' disabled={profile.IsPartner} onClick={newThread}>Connect</Button>
                    </div>
                </StyledCardContent></Card>
                : <ContentLoader preserveAspectRatio='none' style={{height: '10em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>}
                </Grid>
                <Grid size={{ sm: 12, md: 12, lg: 12 }} style={{display: 'flex'}}>
                    {loaded ? <Card style={{flex: 1}}>
                        <Toolbar className='lbtoolbar'>Performance (Last 30 Days)</Toolbar>
                        <StyledCardContent>
                            {profile.TopSeller && <div style={{marginBottom: '1em'}}>
                            <Typography variant='h6' style={{display: 'flex', marginBottom: '0.5rem', position: 'relative'}}><img style={{maxHeight: '2em', maxWidth: '2em', marginRight: '0.5rem'}} src={top_seller} alt=''/> Top Seller</Typography>
                            <Typography>Sellers are awarded a top seller badge based on their order volume within the Logicbroker platform in comparison to the whole seller network.</Typography>
                            </div>}
                            <Typography variant='h6' style={{display: 'flex', marginBottom: '0.5rem', position: 'relative'}}><img style={{maxHeight: '2em', maxWidth: '2em', marginRight: '0.5rem'}} src={shipment_time} alt=''/> On-Time Shipments <span style={{fontSize: '40px', right: '10px', top: '-10px', position: 'absolute'}}>{profile.ShipmentTimeLetter}</span></Typography>
                            <Typography><span style={{fontWeight: 'bold'}}>Average Time to Ship:</span> {profile.ShipmentTime ? formatHoursWithDays(profile.ShipmentTime) : "N/A"}</Typography>
                            <Typography><span style={{fontWeight: 'bold'}}>Target:</span> {profile.ShipmentTimeTarget ? profile.ShipmentTimeTarget + " hours" : "N/A"}</Typography>
                            {profile.ShipmentTimeTarget && <Typography>Shipments are considered to be on time if they are shipped within {profile.ShipmentTimeTarget} hours of the order being received.</Typography>}
                    </StyledCardContent></Card>
                    : <ContentLoader preserveAspectRatio='none' style={{height: '10em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>}
                </Grid>
            </Grid>

            {Number(isRetailer) > 0 && <Fragment>
                <Grid size={{ md: 12 }}>
                {loaded ? <Card>
                    <Toolbar className='lbtoolbar'>Product Catalog</Toolbar>
                    <CardContent style={{display: 'flex'}}>
                        <div style={{flex: '1'}}>
                            <SearchBox onEnter={searchItems} handleChange={updateTerm} placeholder='Search catalog'/>
                        </div>
                    </CardContent>
                </Card> : <ContentLoader preserveAspectRatio='none' style={{height: '6em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>}
                </Grid>
                <Grid size={{ md: 12 }} style={{textAlign: 'left'}}>
                {items.map(item => (
                        <Card key={item.id} className={'product'} onClick={() => { setShowProduct(true); setProduct(item); }}>
                            <ButtonBase>
                                <div>
                                    <div className="productImageHolder"><Image className="productImage" src={getHostedThumbnail(item.image)} altImage={<Image className="productImage" altImage={<NoImageIcon className="productImage"/>} alt={item.name ? `${item.name} image` : ''} src={`${Config.logos}${profile.CoId}large.jpg`}/>} alt={item.Name ? `${item.name} image` : ''}/></div>
                                    <div className="productName">{item.name}</div>
                                </div>
                            </ButtonBase>
                        </Card>
                ))}
                </Grid>
                <Grid size={{ md: 12 }}>
                <TablePagination
                  component="div"
                  count={total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  backIconButtonProps={{
                    'aria-label': 'Previous Page',
                  }}
                  rowsPerPageOptions={[]}
                  nextIconButtonProps={{
                    'aria-label': 'Next Page',
                  }}
                  onPageChange={handleChangePage}
                />
                </Grid>
                </Fragment>}
            <ContactDialog
                open={showMessage}
                onClose={() => setShowMessage(false)}
                disabled={disableSend}
                companyName={profile.CompanyName}
                merchWarning={!(!profile.MerchEmail) && `Merchandising contact ${profile.MerchFirstName || ''} ${profile.MerchLastName || ''} (${profile.MerchEmail}) may not be a Logicbroker user and may not receive your message. Reach out directly through email to ensure they see your message.`}
                message={message}
                messageEmpty={messageEmpty}
                onMessageChange={(html, isEmpty) => { setMessage(html); setMessageEmpty(isEmpty); }}
                submit={createThread}
                sentMessage={messageSent ? 'Message sent! You can continue the conversation in the message center.' : null}
            />
            <Dialog fullWidth={true} maxWidth='sm' open={showProduct} onClose={() => setShowProduct(false)}>
                <Toolbar className='lbtoolbar'>{product.name}</Toolbar>
                <DialogContent>
                    <div style={{fontSize: '0.875rem'}} dangerouslySetInnerHTML={{__html: product.description}}>
                    </div>
                    {product.category ? <Typography style={{marginTop: '1em'}}>
                    Category: {product.category}
                    </Typography> : ''}
                </DialogContent>
            </Dialog>
            </Grid>
    );
}

export default CompanyProfile;

export { ContactDialog }
